/**
 * Selection handlers for ScoopTable
 * Pure functions to handle row selection logic
 */

/**
 * Get AG-Grid row selection mode from ScoopTable selection config
 * @param {Object} selection - Selection configuration
 * @returns {string|undefined} AG-Grid rowSelection value
 */
export const getAgGridSelectionMode = (selection) => {
    if (!selection || selection.mode === 'none') {
        return undefined
    }
    return selection.mode // 'single' or 'multiple'
}

/**
 * Check if row is selectable
 * @param {Object} row - Row data
 * @param {Function} isRowSelectable - User-provided function
 * @returns {boolean}
 */
export const checkRowSelectable = (row, isRowSelectable) => {
    if (!isRowSelectable || typeof isRowSelectable !== 'function') {
        return true
    }
    return isRowSelectable(row)
}

/**
 * Create AG-Grid isRowSelectable callback
 * @param {Function} isRowSelectable - User-provided function (row) => boolean
 * @returns {Function} AG-Grid isRowSelectable callback
 */
export const createAgGridIsRowSelectable = (isRowSelectable) => {
    if (!isRowSelectable) return undefined

    return (params) => {
        return isRowSelectable(params.data)
    }
}

/**
 * Toggle row selection in controlled mode
 * @param {Array} currentSelection - Currently selected rows
 * @param {Object} row - Row to toggle
 * @param {string} mode - Selection mode ('single' | 'multiple')
 * @param {string} idField - Field to use as row ID
 * @returns {Array} Updated selection
 */
export const toggleRowSelection = (currentSelection, row, mode = 'multiple', idField = 'id') => {
    const rowId = row[idField]
    const isSelected = currentSelection.some((r) => r[idField] === rowId)

    if (mode === 'single') {
        return isSelected ? [] : [row]
    }

    // Multiple selection mode
    if (isSelected) {
        return currentSelection.filter((r) => r[idField] !== rowId)
    }
    return [...currentSelection, row]
}

/**
 * Select all rows
 * @param {Array} rows - All rows
 * @param {Function} isRowSelectable - Optional filter function
 * @returns {Array} All selectable rows
 */
export const selectAllRows = (rows, isRowSelectable) => {
    if (!rows || !Array.isArray(rows)) return []

    if (isRowSelectable) {
        return rows.filter((row) => isRowSelectable(row))
    }

    return [...rows]
}

/**
 * Deselect all rows
 * @returns {Array} Empty array
 */
export const deselectAllRows = () => {
    return []
}

/**
 * Check if all selectable rows are selected
 * @param {Array} rows - All rows
 * @param {Array} selectedRows - Currently selected rows
 * @param {Function} isRowSelectable - Optional filter function
 * @param {string} idField - Field to use as row ID
 * @returns {boolean}
 */
export const areAllRowsSelected = (rows, selectedRows, isRowSelectable, idField = 'id') => {
    if (!rows || rows.length === 0) return false
    if (!selectedRows || selectedRows.length === 0) return false

    const selectableRows = isRowSelectable ? rows.filter((row) => isRowSelectable(row)) : rows

    if (selectableRows.length === 0) return false

    return selectableRows.every((row) =>
        selectedRows.some((selected) => selected[idField] === row[idField])
    )
}

/**
 * Check if some (but not all) rows are selected
 * @param {Array} rows - All rows
 * @param {Array} selectedRows - Currently selected rows
 * @param {Function} isRowSelectable - Optional filter function
 * @param {string} idField - Field to use as row ID
 * @returns {boolean}
 */
export const areSomeRowsSelected = (rows, selectedRows, isRowSelectable, idField = 'id') => {
    if (!selectedRows || selectedRows.length === 0) return false

    const allSelected = areAllRowsSelected(rows, selectedRows, isRowSelectable, idField)
    return !allSelected && selectedRows.length > 0
}
